import { ClientEndpoints, Devices, EventTriggers, Facilities, FacilityGroups } from "@app/shared/src/api/api.gen";
import { ClientEndpoint, EventTrigger, Facility, FacilityGroup, FleetDefaults, OperationMode } from "@app/shared/src/models";
import { PassSide } from "@app/shared/src/helpers/zoneNameHelpers";
import { effectiveRelayWiring } from "@app/shared/src/helpers/relayWiring";
import { findCollisionTrigger, findSidePassTrigger, findSpeedingTrigger, setRelayPulseSeconds, triggersForFacility } from "./alertSettings";
import { applyEmailNotification, applyRelaySelection } from "./notificationSettings";
import { applyRadarVideo, radarVideoFromDefaults } from "./radarVideoSettings";
import {
    ensureCollisionTrigger,
    ensureEmailEndpoint,
    ensureProtectedZone,
    ensureRelayEndpoint,
    ensureSidePassTrigger,
    ensureSidePassZone,
    ensureSpeedingTrigger,
    pinToDevice,
} from "./alertProvisioning";
import { ProvisionPlan } from "./addDeviceDraft";

/**
 * Turning a finished wizard draft into a truck the fleet can actually use.
 *
 * Adoption is not one write. A working unit needs a group, a location, the device attached to that
 * location, its mounting recorded, a protected zone, a collision trigger over that zone, and
 * whichever optional alerts were asked for — seven server round-trips that each depend on the last.
 * The old modal refused to attempt this and sent directors to another dashboard, which is defensible
 * only until you notice the alternative: every truck in the fleet configured by hand, differently.
 *
 * The ordering is forced by the data, not by preference: a zone needs a facility and a device to
 * anchor to, and a trigger needs the zone it watches. Steps are reported as they complete so a slow
 * link shows progress rather than a spinner that looks hung.
 *
 * Partial failure is real and is surfaced rather than hidden. If the collision trigger fails after
 * the device is already attached, the truck exists and is adoptable — it is simply missing an alert,
 * which the device's own settings page can add. Rolling back would mean deleting a device a director
 * just successfully claimed, which is worse. So the result names what did land.
 */

export type ProvisionProgress = (message: string) => void;

export type ProvisionResult = {
    deviceId: string;
    facilityId: string;
    facilityGroupId: string;
    /** Anything that did not get created, in words a director can act on. Empty means fully set up. */
    warnings: string[];
};

/** Find the group named, or create it. Returns the id either way. */
async function resolveGroup(plan: ProvisionPlan, organizationId: string): Promise<string> {
    if (plan.group.kind === "existing") return plan.group.id;

    const group = new FacilityGroup();
    group.name = plan.group.name.trim();
    group.organizationId = organizationId;

    const id = await FacilityGroups.save(group);
    if (!id) throw new Error("The group could not be created");
    return id;
}

export async function provisionDevice(
    plan: ProvisionPlan,
    organizationId: string,
    defaults: FleetDefaults,
    onProgress: ProvisionProgress = () => undefined,
): Promise<ProvisionResult> {
    const warnings: string[] = [];

    onProgress(plan.group.kind === "new" ? "Creating the group…" : "Checking the group…");
    const facilityGroupId = await resolveGroup(plan, organizationId);

    // The location IS the truck in this console — one location, one TMA, one device — so it takes
    // the name the director just typed rather than an address.
    onProgress("Creating the location…");
    const facility = new Facility();
    facility.name = plan.truckName;
    facility.facilityGroupId = facilityGroupId;

    const facilityId = await Facilities.save(facility);
    if (!facilityId) throw new Error("The location could not be created");

    onProgress("Attaching the device…");
    await Devices.associateDevice(plan.deviceId, facilityId);

    // Read the device back rather than sending a hand-built one: Devices.save takes the whole model,
    // so posting a sparse object would blank every field the wizard never asked about.
    onProgress("Recording how it is mounted…");
    // A warning, never fatal: the unit is already attached and the zone and alerts below do not
    // depend on this write. Naming the mode matters — without it the truck is not drawn as one.
    const mountingFailed = `${plan.truckName} is attached, but its mounting, attenuator mode and video settings could not be saved — set them on the main dashboard.`;
    const device = await Devices.getById(plan.deviceId).catch(() => undefined);
    if (device) {
        device.name = plan.truckName;
        device.height = plan.heightMetres;
        device.positionX = plan.offsetMetres;
        device.mountedOn = plan.mountedOn;
        // Everything added through this console is a TMA, and the mode is what the rest of the
        // system keys on — the dashboard's attenuator layout view checks it, and a unit left on
        // the factory default (Traffic Counting) renders as a map with no truck at all.
        device.mode = OperationMode.Attenuator_Truck;
        // Fleet recording and streaming, in the same save — without them a new truck runs 0x0.
        // Presets stay out: an id written without its JSON never re-tunes the unit.
        applyRadarVideo(device, {
            ...radarVideoFromDefaults(defaults),
            chirpConfigId: undefined,
            radarTrackerConfigId: undefined,
            videoTrackerConfigId: undefined,
        });
        try {
            await Devices.save(device);
        } catch {
            warnings.push(mountingFailed);
        }
    } else {
        warnings.push(mountingFailed);
    }

    // Reloaded because the zone helpers read facility.devices to anchor a zone to the unit, and the
    // facility we created predates the association.
    let site = await Facilities.getById(facilityId);
    if (!site) throw new Error("The location was created but could not be read back");

    onProgress("Drawing the protected area…");
    const zoned = await ensureProtectedZone(site, plan.widthMetres, plan.lengthMetres);
    if (zoned) site = zoned.facility;
    else warnings.push("The protected area could not be drawn — add it from the truck's settings.");

    onProgress("Setting up impending collision…");
    const collision = await ensureCollisionTrigger({
        facility: site,
        organizationId,
        minApproachSpeedMps: plan.alerts.minApproachSpeedMps,
        reactionTimeSeconds: plan.alerts.reactionTimeSeconds,
        sensitivity: plan.alerts.sensitivity,
    });
    if ("blocked" in collision) {
        warnings.push("Impending collision was not set up because the protected area is missing.");
    }

    // What already applies to this brand-new location. Usually nothing — but a trigger carrying no
    // facility join matches every facility in the org (see EventTriggerController.GetByFacilityId),
    // so an org-wide speeding alert is already governing this truck the moment it exists.
    const inherited =
        (await EventTriggers.getByFacilityId(organizationId, facilityId).catch(() => [] as EventTrigger[])) ?? [];

    if (plan.alerts.speedingEnabled) {
        // Never passed as `existing`. This location is seconds old, so any speeding trigger visible
        // here belongs to somebody else and is merely inherited — updating it would retune every
        // truck it governs as a side effect of adding one, and creating a second would leave the
        // fleet with two org-wide speeding alerts.
        if (findSpeedingTrigger(inherited)) {
            warnings.push("This truck inherits the fleet's existing speeding alert, so its threshold was left alone.");
        } else {
            onProgress("Setting up speeding…");
            try {
                await ensureSpeedingTrigger({
                    facilityId,
                    organizationId,
                    thresholdMps: plan.alerts.speedingThresholdMps,
                    enabled: true,
                });
            } catch {
                warnings.push("The speeding alert could not be created — add it from the truck's settings.");
            }
        }
    }

    const sides: { side: PassSide; enabled: boolean; lanes: number }[] = [
        { side: "left", enabled: plan.alerts.leftPassEnabled, lanes: plan.alerts.leftPassLanes },
        { side: "right", enabled: plan.alerts.rightPassEnabled, lanes: plan.alerts.rightPassLanes },
    ];

    for (const { side, enabled, lanes } of sides) {
        if (!enabled) continue;
        onProgress(`Setting up ${side} pass…`);
        try {
            const zone = await ensureSidePassZone(site, side, lanes);
            if (!zone.ok) {
                warnings.push(`The ${side}-pass zone needs the protected area, which is missing.`);
                continue;
            }
            site = zone.facility;
            // Always a fresh trigger, for the same reason as speeding: a side pass is identified by
            // the zone it watches, so handing over an inherited trigger would repoint another
            // truck's alert at this truck's zone.
            await ensureSidePassTrigger({
                zoneId: zone.zone.id!,
                facilityId,
                organizationId,
                minSpeedMps: plan.alerts.sidePassMinSpeedMps,
                // The fleet's, not the wizard's collision value: a side pass has no reaction-time
                // control of its own, so it follows the default and moves with it.
                reactionTimeSeconds: defaults.driverReactionTimeSeconds,
                enabled: true,
            });
        } catch {
            warnings.push(`The ${side}-pass alert could not be created — add it from the truck's settings.`);
        }
    }

    onProgress("Wiring the beacons, horn and email…");
    warnings.push(...(await wireNotifications(site, organizationId, facilityId, plan, defaults)));

    return { deviceId: plan.deviceId, facilityId, facilityGroupId, warnings };
}

/**
 * Point the truck's new triggers at its relay board and the fleet's email recipients.
 *
 * Skipping this is what made a wizard-onboarded truck warn nobody: a trigger fires the beacons
 * because a row joins it to the WebRelay endpoint and names the terminal, and it emails because a
 * row joins it to an email endpoint — not because a column says so. Every alert above was created
 * without either, so the fleet's stated `collisionRelays`, pulse seconds and `notifyEmailOn…`
 * choices applied to nothing at all until someone happened to save the truck's Notifications page.
 *
 * Done as a pass over the freshly created triggers rather than inline, because the ensure* helpers
 * report whether they created something, not what — and re-reading is one round trip against a
 * location that has at most four triggers on it.
 *
 * Returns warnings rather than throwing: a truck with alerts but no wiring is still a truck a
 * director successfully claimed, and the fix is one screen away.
 */
async function wireNotifications(
    site: Facility,
    organizationId: string,
    facilityId: string,
    plan: ProvisionPlan,
    defaults: FleetDefaults,
): Promise<string[]> {
    const relaysWanted = {
        collision: defaults.collisionRelays,
        speeding: plan.alerts.speedingEnabled ? defaults.speedingRelays : 0,
        left: plan.alerts.leftPassEnabled ? defaults.leftPassRelays : 0,
        right: plan.alerts.rightPassEnabled ? defaults.rightPassRelays : 0,
    };
    const emailWanted = {
        collision: defaults.notifyEmailOnCollision,
        speeding: plan.alerts.speedingEnabled && defaults.notifyEmailOnSpeeding,
        left: plan.alerts.leftPassEnabled && defaults.notifyEmailOnLeftPass,
        right: plan.alerts.rightPassEnabled && defaults.notifyEmailOnRightPass,
    };

    const anyRelays = Object.values(relaysWanted).some(Boolean);
    const anyEmail = Object.values(emailWanted).some(Boolean);
    if (!anyRelays && !anyEmail) return [];

    const warnings: string[] = [];
    const known = (await ClientEndpoints.getAll(organizationId, facilityId).catch(() => [] as ClientEndpoint[])) ?? [];

    const relay = anyRelays
        ? await ensureRelayEndpoint(site, organizationId, defaults.webRelayIpAddress, known).catch(() => undefined)
        : undefined;
    if (anyRelays && !relay) {
        // The fleet has stated no relay address, so there is nothing to point at. Said plainly
        // rather than left as a truck that looks configured and stays silent.
        warnings.push(
            "No relay board address is set for the fleet, so the beacons and horn are not wired to this truck's alerts. Set it under Fleet defaults, then save the truck's Notifications.",
        );
    }

    // Same shape as the relay board: the fleet said who to email, so a new truck comes out
    // emailing them — and a fleet that asked for email while stating no addresses is told so,
    // because an alert that "notifies" nobody is worse than one that plainly doesn't.
    const email = anyEmail
        ? await ensureEmailEndpoint(site, organizationId, defaults.notificationEmailAddresses, known).catch(
              () => undefined,
          )
        : undefined;
    if (anyEmail && !email) {
        warnings.push(
            "The fleet asks for email notifications but no addresses are set, so this truck's alerts email nobody. Set them under Fleet defaults, then save the truck's Notifications.",
        );
    }

    if (!relay && !email) return warnings;

    const triggers = (await EventTriggers.getByFacilityId(organizationId, facilityId).catch(() => [] as EventTrigger[])) ?? [];

    // Only this truck's own. An inherited org-wide trigger belongs to the fleet, and rewiring it
    // here would change what every other truck it governs does.
    const mine = triggersForFacility(triggers, facilityId);

    const targets: { trigger?: EventTrigger; relays: number; email: boolean }[] = [
        { trigger: findCollisionTrigger(mine), relays: relaysWanted.collision, email: emailWanted.collision },
        {
            trigger: plan.alerts.speedingEnabled ? findSpeedingTrigger(mine) : undefined,
            relays: relaysWanted.speeding,
            email: emailWanted.speeding,
        },
        {
            trigger: plan.alerts.leftPassEnabled ? findSidePassTrigger(mine, "left") : undefined,
            relays: relaysWanted.left,
            email: emailWanted.left,
        },
        {
            trigger: plan.alerts.rightPassEnabled ? findSidePassTrigger(mine, "right") : undefined,
            relays: relaysWanted.right,
            email: emailWanted.right,
        },
    ];

    const failed: string[] = [];
    for (const { trigger, relays, email: wantsEmail } of targets) {
        if (!trigger) continue;
        const wiringRelays = Boolean(relays && relay);
        const wiringEmail = Boolean(wantsEmail && email);
        if (!wiringRelays && !wiringEmail) continue;

        try {
            if (wiringRelays) {
                // The fleet's stated wiring decides the terminals — a fleet that swapped horn and
                // lights gets its new trucks wired the way it actually builds them.
                const relayWiring = effectiveRelayWiring(defaults);
                applyRelaySelection(trigger, relays, relay, relayWiring);
                // Pulse durations are the fleet's, same as every other value this wizard starts from.
                setRelayPulseSeconds(trigger, relayWiring.lights.number, plan.alerts.lightsPulseSeconds);
                setRelayPulseSeconds(trigger, relayWiring.horn.number, plan.alerts.hornPulseSeconds);
            }
            if (wiringEmail) {
                applyEmailNotification(trigger, email!, true);
            }
            pinToDevice(trigger);
            await EventTriggers.update(trigger);
        } catch {
            failed.push(trigger.name ?? "an alert");
        }
    }

    return failed.length > 0
        ? [...warnings, `Notifications could not be wired to ${failed.join(", ")} — save the truck's Notifications to retry.`]
        : warnings;
}
